import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ExternalLink, Plus } from 'lucide-react';
import { useAdminEvents } from '../../hooks/useAdminEvents';
import { RouteLink } from '../../lib/router';
import type { EventDraft } from '../../types/events';
import { EventForm } from './EventForm';
import { EventList } from './EventList';
interface AdminWorkspaceProps {
  email: string | null;
  onSignOut: () => void;
}
type Selection = string | 'new' | null;
export function AdminWorkspace({
  email,
  onSignOut
}: AdminWorkspaceProps) {
  const {
    events,
    status,
    error,
    reload,
    save,
    remove
  } = useAdminEvents();
  const [selected, setSelected] = useState<Selection>(null);
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const selectedEvent = selected && selected !== 'new' ? events.find(event => event.id === selected) ?? null : null;

  // If the open event vanished (deleted elsewhere), close the editor.
  useEffect(() => {
    if (status === 'ready' && selected && selected !== 'new' && !selectedEvent) setSelected(null);
  }, [status, selected, selectedEvent]);
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 3200);
    return () => window.clearTimeout(timer);
  }, [notice]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const requestSelect = useCallback((next: Selection) => {
    if (next === selected) return;
    if (dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setDirty(false);
    setSelected(next);
  }, [dirty, selected]);
  const handleSave = useCallback(async (draft: EventDraft) => {
    const saved = await save(selected === 'new' ? null : selected, draft);
    setDirty(false);
    setSelected(saved.id);
    setNotice(saved.published ? 'Published — live on the site now' : 'Saved as a draft');
  }, [save, selected]);
  const handleDelete = useCallback(async () => {
    if (!selected || selected === 'new') return;
    await remove(selected);
    setDirty(false);
    setSelected(null);
    setNotice('Event deleted');
  }, [remove, selected]);
  const handleSignOut = () => {
    if (dirty && !window.confirm('You have unsaved changes. Sign out anyway?')) return;
    onSignOut();
  };
  const editorOpen = selected !== null;
  const formKey = selected === 'new' ? 'new' : `${selectedEvent?.id}:${selectedEvent?.updatedAt}`;
  return <div className="min-h-screen w-full bg-cream text-pine" data-id="element-64" data-mp="2lr8l71zqdzpk">
      <header className="sticky top-0 z-40 border-b border-pine/10 bg-cream/95 backdrop-blur-md" data-id="element-65" data-mp="3j7nr6rgyg0lc">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 md:px-10" data-id="element-66" data-mp="3efxj6s5hpox3">
          <div className="flex min-w-0 items-baseline gap-3" data-id="element-67" data-mp="3lsdiivdzn7nb">
            <RouteLink to="/" className="whitespace-nowrap font-serif text-xl text-pine hover:text-ember" data-id="element-68" data-mp="3lty8s8t9s4fd">
              Pines Retreat
            </RouteLink>
            <span className="hidden text-sm text-clay/70 sm:inline" data-id="element-69" data-mp="3voyjn5mvzylv">Events</span>
            {dirty && <span className="hidden whitespace-nowrap rounded-full border border-gold/60 px-2.5 py-0.5 text-xs text-clay md:inline" data-id="element-70" data-mp="4l1ko2az5abl">
                Unsaved changes
              </span>}
          </div>

          <div className="flex items-center gap-4" data-id="element-71" data-mp="di23ltf9yg7n">
            <RouteLink to="/space#events" className="hidden items-center gap-1.5 whitespace-nowrap text-sm text-clay transition-colors hover:text-pine md:inline-flex" data-id="element-72" data-mp="iallh2kzvxi9">
              View on site
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" data-id="element-73" data-mp="savwcgjk4su9" />
            </RouteLink>
            <div className="hidden text-right text-sm lg:block" data-id="element-74" data-mp="wawbk3ehd82d">
              {email && <span className="text-clay/70" data-id="element-75" data-mp="1ty7z1w0agnoa">{email} · </span>}
              <button type="button" onClick={handleSignOut} className="text-pine hover:text-ember" data-id="element-76" data-mp="1p0o7xvihsgex">
                Sign out
              </button>
            </div>
            <button type="button" onClick={() => requestSelect('new')} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm bg-ember px-4 py-2.5 text-sm font-medium text-cream transition-colors hover:bg-ember/90" data-id="element-77" data-mp="22txfdqmvqrut">
              <Plus className="h-4 w-4" aria-hidden="true" data-id="element-78" data-mp="163eoied632xo" />
              New event
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 py-8 md:px-10 md:py-12 lg:grid-cols-[20rem_1fr] lg:gap-14" data-id="element-79" data-mp="1f1qoh6zxbd2s">
        <aside className={editorOpen ? 'hidden lg:block' : ''} aria-label="Events" data-id="element-80" data-mp="2vf72yr9wtw79">
          <EventList events={events} status={status} error={error} selectedId={selected === 'new' ? null : selected} onSelect={requestSelect} onRetry={reload} data-id="element-81" data-mp="2qi6uyati2jdi" />
          <button type="button" onClick={handleSignOut} className="mt-10 text-sm text-clay/80 hover:text-ember lg:hidden" data-id="element-82" data-mp="32xwsuzd4ayg6">
            Sign out{email ? ` (${email})` : ''}
          </button>
        </aside>

        <main className={editorOpen ? '' : 'hidden lg:block'} data-id="element-83" data-mp="3honluaqg43kv">
          {editorOpen && (selected === 'new' || selectedEvent) ? <EventForm key={formKey} event={selectedEvent} onSave={handleSave} onDelete={handleDelete} onDirtyChange={setDirty} onBack={() => requestSelect(null)} data-id="element-84" data-mp="3mn78kdcv5jea" /> : <div className="flex min-h-[20rem] flex-col items-start justify-center rounded-sm border border-dashed border-pine/20 p-10" data-id="element-85" data-mp="4hcc5u0c1vte">
              <p className="font-serif text-2xl text-pine" data-id="element-86" data-mp="9b6w8yjuoct0">Pick an event to edit</p>
              <p className="mt-2 font-light text-clay" data-id="element-87" data-mp="j4b3loz6wq84">
                Or add the next one with <span className="font-medium text-pine" data-id="element-88" data-mp="e7avz9mozfew">New event</span>.
              </p>
            </div>}
        </main>
      </div>

      <div className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center px-5" role="status" aria-live="polite" data-id="element-89" data-mp="55bfqjji4fd0">
        <AnimatePresence data-id="element-90" data-mp="7t24fujhbyo">
          {notice && <motion.div initial={{
          opacity: 0,
          y: 8,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 8,
          scale: 0.96
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="flex items-center gap-2 rounded-sm bg-pine px-5 py-3 text-sm font-medium text-cream shadow-xl" data-id="element-91" data-mp="3rl5u04yswi5b">
              <Check className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-92" data-mp="38ma5na9mixze" />
              {notice}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}